import React, { useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { IconName } from '../SVGIcon/SVGIcon.types';
import { CardSmallStatsProps } from './CardSmallStats.types';
import { APP_COLORS } from '@/config/colors';

export const CardSmallStats: React.FC<CardSmallStatsProps> = ({
  icon = 'info-ra',
  title,
  subtitle,
  variant = 'default',
  className,
  onClick,
  clickable = false,
  loading = false,
  ...rest
}) => {
  // Base card styles - memoized since they don't change
  const baseStyles = useMemo(
    () =>
      'rounded-card  transition-all border duration-200 flex items-center gap-4 p-5',
    [],
  );

  // Clickable styles - memoized based on clickable and onClick
  const clickableStyles = useMemo(
    () =>
      clickable || onClick
        ? 'cursor-pointer hover:shadow-md hover:border-gray-400 active:scale-[0.98]'
        : '',
    [clickable, onClick],
  );

  // Variant styles - memoized since they're constant
  const variantStyles = useMemo(
    () => ({
      default: 'bg-white border-grayBorder',
      danger: 'bg-white border-legalStatus-dangerBorder',
      warning: 'bg-white border-yellowDark',
      info: 'bg-white  border-blue',
    }),
    [],
  );

  // Card classes - memoized based on dependencies
  const cardClasses = useMemo(
    () =>
      clsx(
        baseStyles,
        clickableStyles,
        {
          [variantStyles[variant]]: variant,
        },
        className,
      ),
    [baseStyles, clickableStyles, variantStyles, variant, className],
  );

  // Icon background class - memoized based on variant
  const iconBackgroundClass = useMemo(() => {
    const colorMap = {
      warning: 'bg-yellowDark',
      info: 'bg-blue',
      danger: 'bg-red-200',
      default: 'bg-[#ECF1F9]',
    };
    return colorMap[variant];
  }, [variant]);

  // Title classes - memoized based on variant
  const titleClasses = useMemo(
    () =>
      clsx('text-sm font-inter font-regular transition-colors duration-200', {
        'text-grayText': variant === 'default',
        'text-warning-contentColor': variant === 'warning',
        'text-blue': variant === 'info',
      }),
    [variant],
  );

  // Value classes - memoized based on variant
  const valueClasses = useMemo(
    () =>
      clsx('font-bold text-base font-inter transition-colors duration-200', {
        'text-text-primary': variant === 'default',
        'text-warning-titleColor': variant === 'warning',
        'text-blue': variant === 'info',
      }),
    [variant],
  );

  // Icon container classes - memoized based on variant
  const iconContainerClasses = useMemo(
    () =>
      clsx(
        'flex-shrink-0 rounded-[10px] flex items-center justify-center transition-all duration-200 w-10 h-10',
        iconBackgroundClass,
      ),
    [iconBackgroundClass],
  );

  // Icon colors - memoized since they're constants
  const iconColor = useMemo(
    () => ({
      warning: APP_COLORS.warning.iconColor,
      danger: APP_COLORS.red[800],
      default: APP_COLORS.primary.DEFAULT,
      info: APP_COLORS.white,
    }),
    [],
  );

  // Handle click - memoized callback
  const handleClick = useCallback(() => {
    if (onClick && !loading) {
      onClick();
    }
  }, [onClick, loading]);

  // Loading icon classes - memoized for loading state
  const loadingIconClasses = useMemo(
    () => clsx(iconContainerClasses, !iconColor && iconBackgroundClass),
    [iconContainerClasses, iconColor, iconBackgroundClass],
  );

  if (loading) {
    return (
      <div className={cardClasses}>
        <div className={loadingIconClasses}>
          <div className="h-6 w-6 animate-pulse rounded-full bg-gray-300" />
        </div>
        <div className="flex-1 space-y-2">
          <div className="h-4 w-24 animate-pulse rounded bg-gray-300" />
          <div className="h-6 w-32 animate-pulse rounded bg-gray-300" />
          {subtitle && (
            <div className="h-3 w-20 animate-pulse rounded bg-gray-300" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(cardClasses, 'group')}
      onClick={handleClick}
      role={clickable || onClick ? 'button' : undefined}
      tabIndex={clickable || onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && (clickable || onClick)) {
          e.preventDefault();
          handleClick();
        }
      }}
      {...rest}
    >
      {/* Icon Container */}
      <div className={iconContainerClasses}>
        <SVGIcon name={icon as IconName} fill={iconColor[variant]} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className={titleClasses}>{title}</div>
        <div className={valueClasses}>{subtitle}</div>
      </div>
    </div>
  );
};
